import { Button, ConfirmSheet, Field, Input, Sheet } from '@didto/ui';
import type { CSSProperties, FormEvent } from 'react';
import { useState } from 'react';
import type { Category } from '@/lib/catalog';
import { useOnline } from '@/lib/network';
import './catalog.css';

export const SWATCHES = [
  '#E07A3F',
  '#3F8FE0',
  '#2E9E6B',
  '#C2452D',
  '#8A63D2',
  '#D9A62B',
  '#4B8B8B',
  '#6B7280',
] as const;

/**
 * Preset colours plus "none"; the server accepts any hex colour, the UI keeps it simple. The chosen
 * one has a ring and a check mark, so the choice does not rest on colour alone.
 */
export function ColorPicker({
  value,
  onChange,
  ariaLabel,
  disabled,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <div className="catalog__swatches" role="group" aria-label={ariaLabel}>
      <button
        type="button"
        className="catalog__swatch catalog__swatch--none"
        aria-pressed={value === null}
        disabled={disabled}
        onClick={() => onChange(null)}
      >
        {value === null && <span aria-hidden>✓</span>}
        None
      </button>
      {SWATCHES.map((c) => {
        const on = value?.toUpperCase() === c;
        return (
          <button
            key={c}
            type="button"
            className="catalog__swatch"
            aria-label={`colour ${c}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(c)}
            style={{ '--swatch': c } as CSSProperties}
          >
            {on && (
              <span className="catalog__swatch-check" aria-hidden>
                ✓
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function CategorySheet({
  category,
  onClose,
  onSave,
  onArchive,
  onRestore,
}: {
  category: Category | null;
  onClose: () => void;
  onSave: (v: { name: string; color: string | null }) => Promise<void>;
  onArchive?: () => Promise<void>;
  onRestore?: () => Promise<void>;
}) {
  // ADR-037: saving, archiving and restoring are commands; none starts without the server
  const online = useOnline();
  const [name, setName] = useState(category?.name ?? '');
  const [color, setColor] = useState<string | null>(category?.color ?? null);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ name: name.trim(), color });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Sheet open onClose={onClose} title={category ? `Edit ${category.name}` : 'New category'}>
        <form onSubmit={(e) => void submit(e)} className="d-stack">
          <Field label="Name" help="Shown as a tab on the POS, e.g. Coffee, Pastry, Study Pods">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                autoFocus
                data-testid="category-name"
              />
            )}
          </Field>
          <div className="d-field">
            <span className="d-label">Colour</span>
            <ColorPicker value={color} onChange={setColor} ariaLabel="category colour" />
          </div>
          <div className="d-spread">
            {onArchive ? (
              <Button
                variant="danger"
                disabled={!online}
                onClick={() => setConfirmArchive(true)}
                data-testid="archive-category"
              >
                Archive
              </Button>
            ) : onRestore ? (
              <Button
                variant="ghost"
                disabled={!online}
                onClick={() => void onRestore()}
                data-testid="restore-category"
              >
                Restore
              </Button>
            ) : (
              <span />
            )}
            {!category?.archived_at && (
              <Button
                type="submit"
                variant="primary"
                busy={busy}
                disabled={!online}
                data-testid="save-category"
              >
                Save
              </Button>
            )}
          </div>
        </form>
      </Sheet>
      {category && onArchive && (
        <ConfirmSheet
          open={confirmArchive}
          title={`Archive ${category.name}?`}
          confirmLabel="Archive category"
          busy={archiving}
          onClose={() => setConfirmArchive(false)}
          onConfirm={() => {
            setArchiving(true);
            // on success the category sheet closes with this one; a refusal is toasted
            void onArchive().catch(() => {
              setArchiving(false);
              setConfirmArchive(false);
            });
          }}
        >
          <p>The category leaves the POS tabs and the filter here.</p>
          <p className="d-muted">
            A category that still holds products cannot be archived: move or archive them first. It
            cannot be brought back from here.
          </p>
        </ConfirmSheet>
      )}
    </>
  );
}
